import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import ApiService from "../../_services/ApiService";
import "./TDETestsList.css";

interface Test {
  id: number;
  name: string;
}

interface RouteParams {
  batchId: string;
}

const TDETestsList: React.FC = () => {
  const [tests, setTests] = useState<Test[]>([]);
  const [batchCode, setBatchCode] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const { batchId } = useParams<RouteParams>();
  const history = useHistory();

  useEffect(() => {
    fetchTests();
  }, [batchId]);

  const fetchTests = async () => {
    try {
      setLoading(true);
      const response: any = await ApiService.getTestsForBatchIds(Number(batchId));

      if (response.data.status === 200) {
        setTests(response.data.tests);
        setBatchCode(response.data.batchCode);
      } else {
        setError("Failed to fetch tests");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching tests");
    } finally {
      setLoading(false);
    }
  };

  const handleTestClick = (testId: number) => {
    // Navigate to test data entry page
    history.push(`/testAnalysis/marks/test/${testId}/batch/${batchId}`);
  };

  const handleBack = () => {
    history.goBack();
  };

  if (loading) {
    return (
      <div className="tde-tests-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading tests…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tde-tests-container">
        <div className="error-container">
          <div className="state-icon is-error" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className="error-message">{error}</div>
          <button className="back-button" onClick={handleBack}>
            <span aria-hidden="true">←</span> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tde-tests-container">
      <div className="header">
        <button className="back-button" onClick={handleBack}>
          <span aria-hidden="true">←</span> Back
        </button>
        <div className="header-content">
          <h1>
            Select Test
            <span className="header-subtitle">Choose a test to enter or review marks</span>
          </h1>
          {batchCode && (
            <p className="batch-code">
              <span className="batch-code-dot" aria-hidden="true" />
              Batch: {batchCode}
            </p>
          )}
        </div>
      </div>

      <div className="tests-grid">
        {tests.length === 0 ? (
          <div className="no-tests">
            <div className="state-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="9" y1="13" x2="15" y2="13" />
                <line x1="9" y1="17" x2="13" y2="17" />
              </svg>
            </div>
            <p>No tests found for this batch</p>
          </div>
        ) : (
          tests.map((test) => (
            <div
              key={test.id}
              className="test-card"
              onClick={() => handleTestClick(test.id)}
            >
              <div className="test-icon" aria-hidden="true">
                {(test.name || "?").trim().charAt(0).toUpperCase()}
              </div>
              <div className="test-content">
                <h3 className="test-name" title={test.name}>{test.name}</h3>
                <span className="test-id">Test ID {test.id}</span>
              </div>
              <div className="test-arrow" aria-hidden="true">→</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TDETestsList;
